// src/pages/PPanel.jsx
import React, {
   useContext,
   useEffect,
   useMemo,
   useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import Footer from "../components/Footer";
import ProfessorGroupsReadonly from "../components/PPanel/ProfessorGroupsReadonly";
import ProfessorStudentsSection from "../components/PPanel/ProfessorStudentsSection";

import { UserContext } from "../UserContext";

import { fetchStudents } from "../store/studentsSlice";
import { fetchGroups } from "../store/groupsSlice";

import {
   getMyGroupStudents,
   getMyGroupOverview,
} from "../api/groupsService";


const STUDENTS_BATCH_SIZE = 16;

function extractMyGroups(res) {
   const groups =
      (Array.isArray(res?.groups) && res.groups) ||
      (Array.isArray(res?.data?.groups) && res.data.groups) ||
      (Array.isArray(res) && res) ||
      [];
   return groups.filter((g) => g && g.id != null);
}

function normalizeMyGroupsStudents(res) {
   // { totalGroups, totalStudents, students:[...] }
   const list = (Array.isArray(res?.students) && res.students) || [];
   return list.filter((s) => s && s.id != null);
}

/* ================= Groups list ================= */
function CourseGroupsList({ groups, apiStudents, status }) {
   return (
      <ProfessorGroupsReadonly
         groups={groups}
         students={apiStudents}
         status={status}
         title="Grupele mele"
      />
   );
}

/* ================= Students list ================= */
function CourseStudentsList({
   students,
   groupNameById,
   loadingAll,
   errorAll,
   status,
   batchSize = STUDENTS_BATCH_SIZE,
}) {
   return (
      <ProfessorStudentsSection
         students={students}
         groupNameById={groupNameById}
         loadingAll={loadingAll}
         errorAll={errorAll}
         status={status}
         batchSize={batchSize}
         title="Studentii mei"
      />
   );
}

function PPanel() {
   const { user } = useContext(UserContext);
   const dispatch = useDispatch();

   const studentsState = useSelector((state) => state.students || {});
   const studentsList = studentsState.list;
   const studentsLoadingRedux = !!studentsState.loading;
   const studentsErrorRedux = studentsState.error || null;

   // (opțional)
   useSelector((state) => state.groups || {});

   const [status, setStatus] = useState("idle"); // idle | loading | ok | none | error
   const [myGroups, setMyGroups] = useState([]);
   const [apiStudents, setApiStudents] = useState([]);

   // 1) redux (opțional pentru îmbogățire)
   useEffect(() => {
      if (!user?.id) return;
      dispatch(fetchStudents());
      dispatch(fetchGroups());
   }, [dispatch, user?.id]);

   // 2) load API: groups + students (NOU!)
   useEffect(() => {
      let cancelled = false;

      (async () => {
         if (!user?.id) return;

         if (String(user?.role).toUpperCase() !== "PROFESSOR") {
            setStatus("error");
            return;
         }

         setStatus("loading");

         try {
            const [studentsRes, overviewRes] = await Promise.all([
               getMyGroupStudents(), // /groups/my-groups/students
               getMyGroupOverview(), // /groups/my-group/overview
            ]);

            if (cancelled) return;

            const groups = extractMyGroups(overviewRes);
            const stList = normalizeMyGroupsStudents(studentsRes);

            setMyGroups(groups);
            setApiStudents(stList);

            // ✅ IMPORTANT: OK dacă ai ORI grupe ORI studenți
            if (groups.length === 0 && stList.length === 0) setStatus("none");
            else setStatus("ok");
         } catch {
            if (cancelled) return;
            setStatus("error");
            setMyGroups([]);
            setApiStudents([]);
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [user?.id, user?.role]);

   // usersById (pentru îmbogățire)
   const usersById = useMemo(() => {
      const list = Array.isArray(studentsList) ? studentsList : [];
      const m = new Map();
      for (const u of list) m.set(String(u?.id), u);
      return m;
   }, [studentsList]);

   // ✅ lista afișată: API students (sigur), îmbogățit cu redux dacă există
   const mergedStudents = useMemo(() => {
      if (status !== "ok" && status !== "none") return [];

      return apiStudents
         .map((s) => {
            const u = usersById.get(String(s?.id));
            return {
               ...s,
               ...(u || {}),
               // păstrează groupId din API (important!)
               groupId: s?.groupId ?? u?.groupId,
            };
         })
         .filter((x) => x && x.id != null);
   }, [apiStudents, usersById, status]);

   // groupId -> name
   const groupNameById = useMemo(() => {
      const m = {};
      for (const g of myGroups) m[String(g.id)] = g?.name || `#${g.id}`;
      return m;
   }, [myGroups]);

   const combinedError =
      status === "error"
         ? "Nu am putut încărca studenții/grupele profesorului."
         : studentsErrorRedux
           ? String(studentsErrorRedux)
           : null;

   return (
      <>

         <main className="main">
            <section className="professor ">
               {/* ✅ toți studenții din my-groups/students */}
               <CourseStudentsList
                  students={mergedStudents}
                  groupNameById={groupNameById}
                  loadingAll={studentsLoadingRedux || status === "loading"}
                  errorAll={combinedError}
                  status={status}
                  batchSize={STUDENTS_BATCH_SIZE}
               />

               {/* ✅ toate grupele din overview */}
               <CourseGroupsList
                  groups={myGroups}
                  apiStudents={apiStudents}
                  status={status}
               />
            </section>

            <Footer />
         </main>
      </>
   );
}

export default PPanel;
